import React, { useMemo, useRef, useState, useCallback } from 'react';
import { useApi } from '../apiHelpers';
import { TextArea } from '../components/ui/Field';
import { useMention } from '../components/ui/ComboboxMention';

interface ActiveUser {
  id: string;
  firstname: string;
  lastname: string | null;
}

interface MentionInputProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  rows?: number;
  className?: string;
}

/**
 * Plain textarea with @-mention autocomplete. The textarea contains the raw
 * Markdown form @[Full Name](user_uuid); the popover-and-insert flow keeps
 * editing simple (regular backspace deletes one char at a time, no chip
 * traversal magic) at the cost of showing the bracketed form during edits.
 *
 * On display, POAItem renders the markdown as first-name chips — so the
 * uglier in-editor form only matters during composition.
 *
 * Trigger rules:
 *   - Typing `@` opens the popover with the current word as the filter.
 *   - The popover stays open while the cursor sits inside the @-token.
 *   - Esc / clicking elsewhere closes it.
 *   - Enter selects the highlighted user (and is intercepted so it does
 *     NOT submit the parent form when the popover is open).
 */
const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus,
  rows = 2,
  className,
}) => {
  const api = useApi();
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Lazy-fetch the user list on first @-keypress.
  const ensureUsers = useCallback(async () => {
    if (usersLoaded) return;
    setUsersLoaded(true);
    try {
      const data: ActiveUser[] = await api.get('/users/active', { silent: true });
      setUsers(data || []);
    } catch {
      setUsers([]);
    }
  }, [api, usersLoaded]);

  const options = useMemo(
    () => users.map((u) => ({ id: u.id, label: u.firstname, sublabel: u.lastname, data: u })),
    [users],
  );

  const mention = useMention({
    inputRef: taRef,
    value,
    onChange,
    options,
    onOpen: ensureUsers,
    breaks: /[[\]()]/,
    insert: (o) => {
      const u = o.data as ActiveUser;
      return `@[${u.firstname}${u.lastname ? ' ' + u.lastname : ''}](${u.id}) `;
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    mention.fieldProps.onKeyDown(e);
    if (e.defaultPrevented) return;
    if (!mention.active && e.key === 'Enter' && !e.shiftKey && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative">
      <TextArea
        ref={taRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        rows={rows}
        className={`!resize-none ${className ?? ''}`}
        {...mention.fieldProps}
        onKeyDown={handleKeyDown}
      />
      {mention.list}
    </div>
  );
};

export default MentionInput;
